/**
 * DebugPanelToggle renders the button to open/close the debug sidebar.
 *
 * Why: Extracting this keeps Header focused on layout and reduces its size.
 * The hover behavior and focus styling are encapsulated here.
 *
 * When to use: Rendered by Header in the right-side button group.
 */
import * as React from 'react'
import { cn } from '@merels/ui'
import { IconButton } from '../../library/IconButton/IconButton'
import { PanelIcon } from '../../library/Icons/PanelIcon'
import { headerTestIds } from './header.testIds'

export interface DebugPanelToggleProps {
  /** Whether the debug panel is currently open */
  isOpen: boolean
  /** Callback to toggle the panel */
  onToggle: () => void
  /** Whether the active game is owned by the signed-in user */
  isOwnedGame?: boolean
  /** Ref for focus management (callback or object ref) */
  buttonRef?: React.Ref<HTMLButtonElement>
  /** Tab index for focus navigation */
  tabIndex?: number
  /** Whether this button is currently focused */
  isFocused?: boolean
  /** Accent color for styling */
  accentColor?: string
  /** Ink when filled with accentColor (hover) */
  accentInkColor?: string
  /** Background color for focus ring */
  backgroundColor?: string
}

export function DebugPanelToggle({
  isOpen,
  onToggle,
  isOwnedGame = false,
  buttonRef,
  tabIndex,
  isFocused,
  accentColor,
  accentInkColor = '#ffffff',
  backgroundColor,
}: DebugPanelToggleProps) {
  const surfaceName = isOwnedGame ? 'Creator Panel' : 'Debug Panel'
  const label = isOpen ? `Close ${surfaceName}` : `Open ${surfaceName}`

  return (
    <IconButton
      ref={buttonRef}
      onClick={onToggle}
      data-testid={headerTestIds.debugSidebarToggle}
      className={cn(
        "flex h-[var(--menu-btn-size)] w-[var(--menu-btn-size)] items-center justify-center rounded-lg transition-colors",
        isFocused && "outline-none"
      )}
      style={{
        ...(accentColor ? { color: accentColor } : {}),
        ...(isFocused && accentColor ? {
          boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
        } : {}),
      }}
      tabIndex={tabIndex}
      title={label}
      onMouseEnter={(e) => {
        if (accentColor) {
          e.currentTarget.style.backgroundColor = accentColor
          e.currentTarget.style.color = accentInkColor
        }
      }}
      onMouseLeave={(e) => {
        if (accentColor) {
          e.currentTarget.style.backgroundColor = 'transparent'
          e.currentTarget.style.color = accentColor
        }
      }}
      aria-label={label}
      icon={<PanelIcon width="var(--menu-icon-size)" height="var(--menu-icon-size)" />}
    />
  )
}
